<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-cubes</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Material <span class="secondary--text">Comprometido</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Planificación y seguimiento de suministros en producción</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-supply-pending :data="supplies"/>
      </div>
    </v-row>

    <!-- Metrics Row -->
    <v-row class="mb-8 px-2" v-if="supplies.length > 0">
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card pa-4 shadow-xs border-left-primary bg-white">
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="caption grey--text font-weight-black uppercase mb-1">Insumos Pendientes</div>
              <div class="text-h4 font-weight-black secondary--text lh-1">
                {{ supplies.filter(s => s.pending > 0).length }}
              </div>
            </div>
            <v-avatar color="primary lighten-5" size="40">
              <v-icon color="primary" small>fas fa-exclamation-circle</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="4">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Periodo de Entrega</div>
          <v-menu
            v-model="menuDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="computedDateFormattedMomentjs"
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                clearable
                @click:clear="clearDate"
                hide-details
                prepend-inner-icon="event"
                class="modern-input"
                placeholder="Rango de fechas"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateRange"
              @change="onDateChange"
              range
              color="primary"
              no-title
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="auto" class="d-flex pb-1">
          <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="supplies"
        :loading="loading"
        :search="search"
        class="modern-table"
        sort-by="pending"
        :sort-desc="true"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Header -->
        <template v-slot:top>
          <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Materiales Comprometidos por Producción ({{ supplies.length }})</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              placeholder="Buscar material o código..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </v-sheet>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="body-2 font-weight-black primary--text lh-1-2 py-2">{{ item.name }}</div>
          <div class="caption grey--text font-weight-bold uppercase" v-if="item.group">{{ item.group }}</div>
        </template>

        <template v-slot:item.unit="{ item }">
          <v-chip x-small outlined label class="grey--text text--darken-2 font-weight-black px-1" style="height: 18px;">
            {{ item.unit }}
          </v-chip>
        </template>

        <template v-slot:item.qty="{ item }">
          <div class="body-2 font-weight-medium grey--text text--darken-2">
            {{ item.qty | number('0,0.00') }}
          </div>
        </template>

        <template v-slot:item.consumed="{ item }">
          <div class="body-2 font-weight-medium grey--text">
            {{ item.consumed | number('0,0.00') }}
          </div>
        </template>

        <template v-slot:item.pending="{ item }">
          <div class="d-flex flex-column align-end">
            <span class="body-1 font-weight-black tracking-tighter" :class="item.pending > 0 ? 'secondary--text' : 'grey--text opacity-40'">
              {{ item.pending | number('0,0.00') }}
            </span>
          </div>
        </template>

        <template v-slot:item.article_sku="{ item }">
          <div class="d-flex align-center" v-if="item.article_sku">
            <v-chip 
              x-small label dark color="secondary" 
              class="font-weight-black clickable-chip"
              @click.stop="$router.push({name:'DetailedArticle',params:{id:item.article_id}})"
            >
              {{ item.article_sku }}
            </v-chip>
            <v-btn icon x-small class="ml-1" @click.stop="$router.push({name:'DetailedArticle',params:{id:item.article_id}})">
              <v-icon x-small color="grey lighten-1">fas fa-external-link-alt</v-icon>
            </v-btn>
          </div>
          <span v-else class="caption grey--text italic opacity-40">No vinculado</span>
        </template>

        <template v-slot:item.stock="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip bottom :color="item.stock < 0 ? 'error' : item.stock < item.pending ? 'warning' : 'success'">
              <template v-slot:activator="{ on, attrs }">
                <v-chip 
                  v-bind="attrs" v-on="on"
                  small label outlined 
                  :color="item.stock < 0 ? 'error' : item.stock < item.pending ? 'warning' : 'success'"
                  class="font-weight-black"
                >
                  {{ item.stock | number('0,0.00') }}
                </v-chip>
              </template>
              <span>Stock actual en bodega</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import {fetchSupplyOrders} from '@/api/reports'
import SalesProductGraph from '@/components/reports/SalesProductGraph'
import ExportSupplyPending from '@/components/reports/ExportSupplyPending'
import moment from 'moment'
import notifications from '@/utils/notifications'

export default {
  name: "SupplyOrdersPendingReport",
  components: {
    SalesProductGraph,
    ExportSupplyPending
  },
  data() {
    return {
      loading: false,
      search: '',
      supplies: [],
      headers: [
        { text: 'Descripción Material', align: 'start', value: 'name', class: 'table-header' },
        { text: 'UM', align: 'center', value: 'unit', class: 'table-header', width: '80px' },
        { text: 'Comprometido', align: 'end', value: 'qty', class: 'table-header', width: '130px' },
        { text: 'Consumido', align: 'end', value: 'consumed', class: 'table-header', width: '130px' },
        { text: 'Pendiente', align: 'end', value: 'pending', class: 'table-header', width: '130px' },
        { text: 'Artículo Vinculado', align: 'start', value: 'article_sku', class: 'table-header', width: '160px' },
        { text: 'Stock Bodega', align: 'end', value: 'stock', class: 'table-header', width: '120px' },
      ],
      groups: [],
      filter_group: '',
      menuDate: false,
      dateRange: ['', '']
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      let delivery_after = ''
      let delivery_before = ''
      if (this.dateRange[0]) delivery_after = moment(this.dateRange[0]).utc().startOf('day').format('YYYY-MM-DD HH:mm')
      if (this.dateRange[1]) delivery_before = moment(this.dateRange[1]).utc().endOf('day').format('YYYY-MM-DD HH:mm')

      const query = `product__completed=false&delivery_after=${delivery_after}&delivery_before=${delivery_before}`
      
      try {
        const res = await fetchSupplyOrders(query)
        this.supplies = this.processData(res)
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.loading = false
      }
    },
    onDateChange() {
      this.menuDate = false
      this.fetch()
    },
    processData(rawSupplies) {
      let uniqueNames = [...new Set(rawSupplies.map(item => item.name))];
      this.groups = [...new Set(rawSupplies.map(item => item.group_name))];

      let results = uniqueNames.map(name => {
        const items = rawSupplies.filter(p => p.name == name)
        const last = items[items.length - 1]
        const total_qty = items.reduce((acc, val) => acc + parseFloat(val.total_pp || 0), 0)
        const total_consumed = items.reduce((acc, val) => acc + parseFloat(val.consumed || 0), 0)
        
        return {
          name: last.name,
          sku: last.sku,
          group: last.group_name,
          unit: last.unit_name,
          article_sku: last.article ? last.article.sku : null,
          article_id: last.article ? last.article.id : null,
          stock: last.article ? last.article.stocks.filter(i => i.add_stock).reduce((acc, val) => acc + parseFloat(val.qty || 0), 0) : 0,
          qty: total_qty,
          consumed: total_consumed,
          pending: total_qty - total_consumed
        }
      })
      
      if (this.filter_group) {
        results = results.filter(s => s.group == this.filter_group)
      }
      return results
    },
    clearDate() {
      this.dateRange = ['', '']
      this.fetch()
    },
  },
  computed: {
    computedDateFormattedMomentjs () {
      if (!this.dateRange[0]) return ''
      const start = moment(this.dateRange[0]).format('DD/MM/YYYY')
      const end = this.dateRange[1] ? moment(this.dateRange[1]).format('DD/MM/YYYY') : '...'
      return `${start} - ${end}`
    },
  },
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }

/* Modern Card Styles */
.modern-card { 
  border-radius: 12px !important; 
  border: 1px solid rgba(0,0,0,0.05) !important; 
  background-color: white !important; 
}
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 56px !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}
.modern-table >>> td {
  height: 60px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
}
.modern-search-field { max-width: 400px; }
.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 12px !important;
}

/* Utilities */
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.0px; }
.border-white-sm { border: 2px solid white !important; }
.clickable-chip { cursor: pointer; transition: opacity 0.2s; }
.clickable-chip:hover { opacity: 0.8; }
.opacity-40 { opacity: 0.4; }
.italic { font-style: italic; }
</style>